import { useState, useEffect, useCallback } from "react";
import { API, LOGO_URL } from "@/config";
import apiClient, { setAuthToken, clearAuthToken } from "@/utils/apiClient";

const AuthContext = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await apiClient.get(`${API}/auth/me`);
      setUser(response.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const response = await apiClient.post(`${API}/auth/login`, { email, password });
    if (response.data.access_token) setAuthToken(response.data.access_token);
    setUser(response.data.user);
    return response.data;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const response = await apiClient.post(`${API}/auth/register`, { email, password, name });
    if (response.data.access_token) setAuthToken(response.data.access_token);
    setUser(response.data.user);
    return response.data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiClient.post(`${API}/auth/logout`);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Logout request failed, clearing local session anyway:', error);
      }
    }
    clearAuthToken();
    setUser(null);
  }, []);

  if (loading) {
    return (
      <div className="finance-app min-h-screen flex items-center justify-center" data-testid="loading-screen">
        <div className="text-center">
          <img src={LOGO_URL} alt="SubiWealth" className="h-16 mx-auto mb-4" />
          <div className="w-16 h-16 border-4 border-teal-200 border-t-teal-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-teal-600 font-medium">Loading SubiWealth...</p>
        </div>
      </div>
    );
  }

  return children({ user, setUser, login, register, logout, loading });
};

export default AuthContext;
